/*
 * Planigo
 * Auteur  : Stéphane Raverdy
 * Société : SCIAD 2026
 */
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { ConfigProvider } from "./contexts/ConfigContext";
import { Toaster } from "./components/ui/sonner";

// Components
import ProtectedRoute from "./components/ProtectedRoute";
import MainLayout from "./components/MainLayout";
import AppErrorBoundary from "./components/AppErrorBoundary";
import ClientErrorReporter from "./components/ClientErrorReporter";

// Pages
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import ProjectsPage from "./pages/ProjectsPage";
import ProjectDashboardPage from "./pages/ProjectDashboardPage";
import TasksPage from "./pages/TasksPage";
import WorkloadPage from "./pages/WorkloadPage";
import AbsencesPage from "./pages/AbsencesPage";
import RequirementsPage from "./pages/RequirementsPage";
import TimeTrackingPage from "./pages/TimeTrackingPage";
import TestsPage from "./pages/TestsPage";
import TeamsPage from "./pages/TeamsPage";
import SprintsMilestonesPage from "./pages/SprintsMilestonesPage";
import AdminPage from "./pages/AdminPage";
import SettingsPage from "./pages/SettingsPage";
import InvoicingPage from "./pages/InvoicingPage";
import ExpensesPage from "./pages/ExpensesPage";
import ValidationsPage from "./pages/ValidationsPage";
import AboutPage from "./pages/AboutPage";
import BIPage from "./pages/BIPage";
import GovernancePage from "./pages/GovernancePage";
import TechnicalPage from "./pages/TechnicalPage";
import InterventionsPage from "./pages/InterventionsPage";
import InterventionDetailPage from "./pages/InterventionDetailPage";
import AssetsPage from "./pages/AssetsPage";
import AssetsStockHubPage from "./pages/AssetsStockHubPage";
import GmaoPage from "./pages/GmaoPage";
import SuppliersPage from "./pages/SuppliersPage";
import SupplierInvoicesPage from "./pages/SupplierInvoicesPage";
import NotificationsPage from "./pages/NotificationsPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import PublicInterventionPage from "./pages/PublicInterventionPage";
import PublicSavPage from "./pages/PublicSavPage";
import useCompactMode from "./hooks/useCompactMode";
import useMobileWorkspace from "./hooks/useMobileWorkspace";
import CompactProjectsPage from "./pages/compact/CompactProjectsPage";
import CompactProjectPage from "./pages/compact/CompactProjectPage";
import CompactTasksPage from "./pages/compact/CompactTasksPage";
import CompactTimePage from "./pages/compact/CompactTimePage";
import CompactSprintsPage from "./pages/compact/CompactSprintsPage";
import CompactTestsPage from "./pages/compact/CompactTestsPage";
import CompactTechnicianHome from "./pages/compact/CompactTechnicianHome";
import CompactInterventionsPage from "./pages/compact/CompactInterventionsPage";
import CompactAssetsPage from "./pages/compact/CompactAssetsPage";

function AppRouter() {
  const compact = useCompactMode();
  const mobileWorkspace = useMobileWorkspace();
  // REGLE DE GESTION : la connexion Planigo reste locale. Google Agenda est
  // rattache uniquement depuis le profil d'un utilisateur deja authentifie.
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/public/intervention/:token" element={<PublicInterventionPage />} />
      <Route path="/public/sav/:token" element={<PublicSavPage />} />
          
          {/* Protected routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <MainLayout>
                  {mobileWorkspace.technicianMode ? <CompactTechnicianHome /> : <DashboardPage />} 
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects"
            element={
              <ProtectedRoute>
                <MainLayout>
                  {compact ? <CompactProjectsPage /> : <ProjectsPage />}
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/interventions"
            element={
              <ProtectedRoute>
                <MainLayout>{compact ? <CompactInterventionsPage /> : <InterventionsPage />}</MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/interventions/:interventionId"
            element={
              <ProtectedRoute module="/interventions">
                <MainLayout><InterventionDetailPage /></MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/assets"
            element={
              <ProtectedRoute>
                <MainLayout>{compact ? <CompactAssetsPage /> : <AssetsStockHubPage initialView="park" />}</MainLayout>
              </ProtectedRoute>
            }
          />
          <Route path="/gmao" element={<ProtectedRoute module="/assets"><MainLayout><GmaoPage mode="dashboard" /></MainLayout></ProtectedRoute>} />
          <Route path="/sav" element={<ProtectedRoute module="/assets"><MainLayout>{compact ? <CompactAssetsPage /> : <AssetsPage initialTab="sav" />}</MainLayout></ProtectedRoute>} />
          <Route path="/loans" element={<ProtectedRoute module="/assets"><MainLayout>{compact ? <CompactAssetsPage /> : <AssetsPage initialTab="loans" />}</MainLayout></ProtectedRoute>} />
          <Route path="/audits" element={<ProtectedRoute module="/assets"><MainLayout>{compact ? <CompactAssetsPage /> : <AssetsPage initialTab="audits" />}</MainLayout></ProtectedRoute>} />
          <Route path="/store" element={<ProtectedRoute module="/assets"><MainLayout><AssetsStockHubPage initialView="store" /></MainLayout></ProtectedRoute>} />
          <Route path="/inventory" element={<ProtectedRoute module="/assets"><MainLayout><GmaoPage mode="inventory" /></MainLayout></ProtectedRoute>} />
          <Route path="/suppliers" element={<ProtectedRoute module="/assets"><MainLayout><SuppliersPage /></MainLayout></ProtectedRoute>} />
          <Route path="/purchase-orders" element={<ProtectedRoute module="/assets"><MainLayout><GmaoPage mode="orders" /></MainLayout></ProtectedRoute>} />
          <Route path="/supplier-invoices" element={<ProtectedRoute module="/assets"><MainLayout><SupplierInvoicesPage /></MainLayout></ProtectedRoute>} />
          <Route path="/maintenance-planning" element={<ProtectedRoute module="/assets"><MainLayout><GmaoPage mode="planning" /></MainLayout></ProtectedRoute>} />
          <Route path="/gmao-reporting" element={<ProtectedRoute module="/assets"><MainLayout><GmaoPage mode="reporting" /></MainLayout></ProtectedRoute>} />
          <Route path="/technical" element={<ProtectedRoute module="/technical"><MainLayout><TechnicalPage /></MainLayout></ProtectedRoute>} />
          <Route
            path="/projects/:projectId"
            element={
              <ProtectedRoute module="/projects">
                <MainLayout>
                  {compact ? <CompactProjectPage /> : <ProjectDashboardPage />}
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tasks"
            element={
              <ProtectedRoute>
                <MainLayout>
                  {compact ? <CompactTasksPage /> : <TasksPage />}
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/absences"
            element={
              <ProtectedRoute>
                <MainLayout>
                  {compact ? <Navigate to="/" replace /> : <AbsencesPage />}
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/workload"
            element={
              <ProtectedRoute>
                <MainLayout>
                  {compact ? <Navigate to="/" replace /> : <WorkloadPage />}
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/requirements"
            element={
              <ProtectedRoute>
                <MainLayout>
                  {compact ? <Navigate to="/" replace /> : <RequirementsPage />}
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/time"
            element={
              <ProtectedRoute>
                <MainLayout>
                  {compact ? <CompactTimePage /> : <TimeTrackingPage />}
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tests"
            element={
              <ProtectedRoute>
                <MainLayout>
                  {compact ? <CompactTestsPage /> : <TestsPage />}
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/teams"
            element={
              <ProtectedRoute>
                <MainLayout>
                  {compact ? <Navigate to="/" replace /> : <TeamsPage />}
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/sprints"
            element={
              <ProtectedRoute>
                <MainLayout>
                  {compact ? <CompactSprintsPage /> : <SprintsMilestonesPage />}
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <MainLayout>
                  {compact ? <Navigate to="/" replace /> : <AdminPage />}
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <MainLayout>
                  {compact ? <Navigate to="/" replace /> : <SettingsPage />}
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoicing"
            element={
              <ProtectedRoute>
                <MainLayout>
                  {compact ? <Navigate to="/" replace /> : <InvoicingPage />}
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/expenses"
            element={
              <ProtectedRoute>
                <MainLayout>
                  {compact ? <Navigate to="/" replace /> : <ExpensesPage />}
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/validations"
            element={
              <ProtectedRoute>
                <MainLayout>
                  {compact ? <Navigate to="/" replace /> : <ValidationsPage />}
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/governance"
            element={
              <ProtectedRoute>
                <MainLayout>
                  {compact ? <Navigate to="/" replace /> : <GovernancePage />}
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/bi"
            element={
              <ProtectedRoute>
                <MainLayout>
                  {compact ? <Navigate to="/" replace /> : <BIPage />}
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <MainLayout>
                  {compact ? <Navigate to="/" replace /> : <NotificationsPage />}
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/about"
            element={
              <ProtectedRoute>
                <MainLayout>
                  {compact ? <Navigate to="/" replace /> : <AboutPage />}
                </MainLayout>
              </ProtectedRoute>
            }
          />
          
          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <ConfigProvider>
        <ThemeProvider>
          <BrowserRouter>
            <ClientErrorReporter />
            <AppErrorBoundary>
              <AppRouter />
            </AppErrorBoundary>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </ThemeProvider>
      </ConfigProvider>
    </AuthProvider>
  );
}

export default App;
